// Considered: AddSphereConfigureStepMolecule — rejected because Deploy mode requires a distinct
// affordance: target-lock bar, applicability hint, source toggle (Subscribed/Marketplace),
// eligible-sphere target picker, and deploy CTA. ConfigureStep's contract is sphere creation,
// not approach deployment. See spec Reuse Audit (specs/add-sphere-overhaul.md).
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'localization';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { useImpersonation } from '@center-technologies/shared-api-clients';
import { useAnalytics } from '@center-technologies/analytics';
import { useTemplateApproaches } from '../../../hooks/useTemplateApproaches';
import { useEligibleApproaches } from '../../../hooks/useEligibleApproaches';
import { useChannels } from '../../../hooks/useChannels';
import { useSphereArchetypes } from '../../../hooks/useSphereArchetypes';
import { groupApproachesBySuggestion } from './groupApproachesBySuggestion';
import ChipAtom from '../../atoms/Chip/ChipAtom';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import SegmentedControlAtom from '../../atoms/SegmentedControl/SegmentedControlAtom';
import SpinnerAtom from '../../atoms/Spinner/SpinnerAtom';
import EmptyStateAtom from '../../atoms/EmptyState/EmptyStateAtom';
import SagaProgressMolecule from '../SagaProgress/SagaProgressMolecule';
import { useSagaProgress } from '../../../hooks/useSagaProgress';
import type { Sphere } from 'types/global';
import type { LocalizedString } from 'types/context';
import type { TemplateApproach } from '../../../types/templateApproach';
import { getEligibleSpheres, getDeploymentProgress, deployApproach, type EligibleSphere } from '../../../api/marketplaceApi';
import './DeployApproachStepMolecule.scss';

function resolveLocalizedName(name: LocalizedString | string | null | undefined): string {
  if (!name) return '';
  if (typeof name === 'string') return name;
  return name.default ?? name.translations?.['en-US'] ?? '';
}

export interface DeployApproachStepMoleculeProps {
  /** Pre-locked target sphere (from row-menu "Deploy approach"). null = no pre-target. */
  targetSphere: Sphere | null;
  /** Called when the user clicks "Change sphere" to unlock the target. */
  onChangeSphere: () => void;
  /**
   * Called when the user leaves the post-deploy view with *Done*, with the deploymentId.
   * After a successful deploy the molecule stays mounted on that view — live progress for a
   * running saga (202), the completed summary for a synchronous one (200) — so this fires on
   * the user's dismissal, never on the deploy response itself.
   */
  onDeployComplete: (deploymentId: string) => void;
  /**
   * Opens the sphere the approach was deployed onto. When provided, *Open sphere* is the
   * primary action once the deployment has completed; omit it and the post-deploy view offers
   * *Done* only.
   */
  onOpenSphere?: (sphereId: string) => void;
  onClose: () => void;
}

type Source = 'subscribed' | 'marketplace';

interface DeployedState {
  deploymentId: string;
  sphereId: string;
  /** The deploy answered 200: every step finished inside the request. */
  synchronous: boolean;
}

const FINISHED_STATUSES = new Set(['completed', 'failed', 'compensated']);

const DeployApproachStepMolecule: React.FC<DeployApproachStepMoleculeProps> = ({
  targetSphere,
  onChangeSphere,
  onDeployComplete,
  onOpenSphere,
  onClose,
}) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();
  const { impersonationDetails } = useImpersonation();
  const organizationId = impersonationDetails?.organizationId ?? '';
  const logAnalyticsEvent = useAnalytics();

  const [source, setSource] = useState<Source>('subscribed');
  const [selectedChannelId, setSelectedChannelId] = useState<string>('all');
  const [query, setQuery] = useState('');
  const [selectedApproach, setSelectedApproach] = useState<TemplateApproach | null>(null);

  // lockedSphere: starts from the prop; can be selected from eligible-sphere picker
  const [lockedSphere, setLockedSphere] = useState<Sphere | null>(targetSphere);

  // eligible spheres loaded after approach selection (when no lockedSphere)
  const [eligibleSpheres, setEligibleSpheres] = useState<EligibleSphere[]>([]);
  const [eligibleLoading, setEligibleLoading] = useState(false);

  const [isDeploying, setIsDeploying] = useState(false);
  const [deployError, setDeployError] = useState<string | null>(null);
  // Set once the deploy call succeeds — switches the body to the post-deploy view
  // (SagaProgressMolecule + Done / Open sphere) until the user leaves it.
  const [deployed, setDeployed] = useState<DeployedState | null>(null);

  const { approaches: templateApproaches, loading: templateLoading } = useTemplateApproaches();
  // Sphere-first (locked) path: fetch approaches eligible for the locked sphere server-side.
  // Approach-first path (targetSphere=null): hook receives null and returns empty immediately.
  const { approaches: eligibleApproaches, loading: eligibleApproachesLoading } = useEligibleApproaches(
    targetSphere ? (lockedSphere?.id ?? null) : null,
  );
  const approaches = targetSphere ? eligibleApproaches : templateApproaches;
  const approachesLoading = targetSphere ? eligibleApproachesLoading : templateLoading;
  // Archetype display names for the "Suggested for …" headings; only the sphere-first path has suggestions.
  const { archetypes: assignedArchetypes } = useSphereArchetypes(targetSphere ? lockedSphere?.id : undefined);
  const { channels } = useChannels({ subscribed: source === 'subscribed' ? true : undefined });

  // Stable ref for the graphClient — avoids infinite re-render loops when the
  // apiClients object is recreated on every render.
  const graphClientRef = React.useRef(apiClients?.graphClient);
  graphClientRef.current = apiClients?.graphClient;

  const subscribedChannelIds = useMemo(
    () => new Set(channels.filter((c) => c.is_subscribed).map((c) => c.id)),
    [channels],
  );

  const visibleApproaches = useMemo(() => {
    let list = approaches;
    // Locked-sphere path: approaches are pre-filtered by applicability server-side (useEligibleApproaches).
    // Approach-first path: show all template approaches; sphere selection doesn't filter by type.
    // Filter by source
    if (source === 'subscribed') {
      list = list.filter((a) => !a.channel || subscribedChannelIds.has(a.channel.id));
    }
    // Filter by channel
    if (selectedChannelId !== 'all') {
      list = list.filter((a) => a.channel?.id === selectedChannelId);
    }
    // Filter by query
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter((a) => (a.name + a.description).toLowerCase().includes(q));
    }
    return list;
  }, [approaches, source, subscribedChannelIds, selectedChannelId, query]);

  const approachGroups = useMemo(() => {
    const nameOf = (systemName: string) =>
      assignedArchetypes.find((a) => a.systemName === systemName)?.displayName ?? systemName;
    return groupApproachesBySuggestion(visibleApproaches, {
      primary: assignedArchetypes.find((a) => a.isPrimary)?.systemName ?? null,
      suggestedForLabel: (systemName) => t('deploy.suggestedFor', { name: nameOf(systemName) }),
      otherLabel: t('deploy.otherApproaches'),
    });
  }, [visibleApproaches, assignedArchetypes, t]);

  // Fetch eligible spheres when approach selected but no locked sphere.
  // Keyed on selectedApproach.system_name + lockedSphere to avoid triggering
  // on graphClient reference changes (which would cause infinite loops).
  const selectedApproachName = selectedApproach?.system_name ?? null;
  const lockedSphereId = lockedSphere?.id ?? null;
  useEffect(() => {
    if (!selectedApproachName || lockedSphereId || !graphClientRef.current || !organizationId) {
      setEligibleSpheres((prev) => (prev.length === 0 ? prev : []));
      return;
    }
    let cancelled = false;
    setEligibleLoading(true);
    getEligibleSpheres(graphClientRef.current, {
      approachSystemName: selectedApproachName,
      limit: 20,
    })
      .then((res) => {
        if (!cancelled) setEligibleSpheres(res.spheres);
      })
      .catch(() => {
        if (!cancelled) setEligibleSpheres([]);
      })
      .finally(() => {
        if (!cancelled) setEligibleLoading(false);
      });
    return () => { cancelled = true; };
  }, [selectedApproachName, lockedSphereId, organizationId]);

  const handleSelectApproach = useCallback((approach: TemplateApproach) => {
    setSelectedApproach((prev) => (prev?.system_name === approach.system_name ? null : approach));
    setDeployError(null);
  }, []);

  const handleSelectEligibleSphere = useCallback((sphere: EligibleSphere) => {
    setLockedSphere({
      id: sphere.id,
      label: sphere.label,
      glyph: sphere.glyph,
      glyphColor: sphere.glyphColor,
      properties: { name: sphere.name, type: sphere.type },
    } as Sphere);
  }, []);

  const handleChangeSphere = useCallback(() => {
    setLockedSphere(null);
    setEligibleSpheres([]);
    onChangeSphere();
  }, [onChangeSphere]);

  const handleSourceChange = useCallback((value: string | string[]) => {
    const v = Array.isArray(value) ? value[0] : value;
    setSource(v as Source);
    setSelectedChannelId('all');
  }, []);

  const handleDeploy = useCallback(async () => {
    if (!selectedApproach || !lockedSphere || !graphClientRef.current) return;
    setIsDeploying(true);
    setDeployError(null);
    try {
      const result = await deployApproach(graphClientRef.current, {
        sphereId: lockedSphere.id,
        approachSystemName: selectedApproach.system_name,
      });
      logAnalyticsEvent('approach_deployed', undefined, {
        approachSystemName: selectedApproach.system_name,
        sphereId: lockedSphere.id,
        channelSystemName: selectedApproach.channel?.system_name,
        suggestedByArchetype: (selectedApproach.suggestedBy?.length ?? 0) > 0,
      });
      setDeployed({
        deploymentId: result.deploymentId,
        sphereId: lockedSphere.id,
        synchronous: result.status === 'completed',
      });
    } catch {
      setDeployError(t('deploy.errors.deployFailed'));
    } finally {
      setIsDeploying(false);
    }
  }, [selectedApproach, lockedSphere, logAnalyticsEvent, t]);

  const deployedId = deployed?.deploymentId ?? null;
  const progressFetcher = useCallback(() => {
    if (!deployedId || !graphClientRef.current) return Promise.resolve(null);
    return getDeploymentProgress(graphClientRef.current, deployedId);
  }, [deployedId]);

  const {
    progress: sagaProgress,
    loading: sagaProgressLoading,
    error: sagaProgressError,
    deniedReason: sagaProgressDeniedReason,
    refetch: refetchSagaProgress,
  } = useSagaProgress(progressFetcher, { active: !!deployedId });

  const handleDone = useCallback(() => {
    if (deployed) onDeployComplete(deployed.deploymentId);
  }, [deployed, onDeployComplete]);

  const handleOpenSphere = useCallback(() => {
    if (deployed && onOpenSphere) onOpenSphere(deployed.sphereId);
  }, [deployed, onOpenSphere]);

  const sphereName = lockedSphere?.properties?.name ?? '';
  const canDeploy = !!selectedApproach && !!lockedSphere && !isDeploying;

  if (deployed) {
    const completed = deployed.synchronous || sagaProgress?.status === 'completed';
    const finished = completed || (!!sagaProgress && FINISHED_STATUSES.has(sagaProgress.status));
    // A synchronous deploy is known to be done; if its summary can't be read, say so plainly
    // rather than show the molecule's "setup is still running" error copy.
    const summaryUnavailable =
      deployed.synchronous && !sagaProgress && (!!sagaProgressError || !!sagaProgressDeniedReason);
    return (
      <div className="deploy-approach-step" data-testid="deploy-approach-step">
        <div className="deploy-approach-step__progress">
          {summaryUnavailable ? (
            <output className="deploy-approach-step__completed-note" data-testid="deploy-completed-note">
              {t('deploy.progress.completed')}
            </output>
          ) : (
            <SagaProgressMolecule
              progress={sagaProgress}
              loading={sagaProgressLoading}
              error={sagaProgressError}
              deniedReason={sagaProgressDeniedReason}
              onRetry={refetchSagaProgress}
              variant="inline"
              surface="deploy_dialog"
            />
          )}
          <div className="deploy-approach-step__detail-footer deploy-approach-step__progress-footer">
            <ButtonAtom
              label={t('common.actions.done')}
              variant={completed && onOpenSphere ? 'secondary' : 'primary'}
              onClick={handleDone}
            />
            {completed && onOpenSphere && (
              <ButtonAtom
                label={t('deploy.progress.openSphere')}
                icon="pi pi-arrow-right"
                onClick={handleOpenSphere}
              />
            )}
          </div>
          {!finished && (
            <p className="deploy-approach-step__background-note" data-testid="deploy-continues-note">
              {t('deploy.progress.continuesInBackground')}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="deploy-approach-step" data-testid="deploy-approach-step">
      {/* Target lock bar */}
      {lockedSphere && (
        <div className="deploy-approach-step__lock-bar">
          <span className="deploy-approach-step__lock-label">
            {t('deploy.targetLock.label')}
          </span>
          <div className="deploy-approach-step__lock-sphere">
            {lockedSphere.glyph && (
              <i className={`deploy-approach-step__lock-glyph ${lockedSphere.glyph}`} />
            )}
            <div className="deploy-approach-step__lock-info">
              <div className="deploy-approach-step__lock-name">{sphereName}</div>
              <div className="deploy-approach-step__lock-type">{lockedSphere.properties.type}</div>
            </div>
            <ChipAtom label={t('deploy.targetLock.locked')} icon="pi pi-lock" />
          </div>
          <ButtonAtom
            label={t('deploy.targetLock.change')}
            icon="pi pi-sync"
            variant="secondary"
            size="small"
            onClick={handleChangeSphere}
          />
          <span className="deploy-approach-step__applicability-hint">
            {t('deploy.applicabilityHint', { schemaName: lockedSphere.properties.type })}
          </span>
        </div>
      )}

      {/* Body */}
      <div className="deploy-approach-step__body">
        {/* Source rail */}
        <aside className="deploy-approach-step__rail" aria-label={t('deploy.rail.ariaLabel')}>
          <SegmentedControlAtom
            options={[
              { value: 'subscribed', label: t('deploy.source.subscribed') },
              { value: 'marketplace', label: t('deploy.source.marketplace') },
            ]}
            value={source}
            onChange={handleSourceChange}
            mode="single"
            variant="strip"
            size="small"
            className="deploy-approach-step__source-toggle"
          />
          <div className="deploy-approach-step__channel-label">
            {source === 'subscribed' ? t('deploy.channels.yourChannels') : t('deploy.channels.allChannels')}
          </div>
          <button
            type="button"
            className={`deploy-approach-step__channel-btn${selectedChannelId === 'all' ? ' deploy-approach-step__channel-btn--active' : ''}`}
            onClick={() => setSelectedChannelId('all')}
          >
            <i className="pi pi-globe" />
            <span>{t('deploy.channels.allSources')}</span>
            <span className="deploy-approach-step__channel-count">{visibleApproaches.length}</span>
          </button>
          {channels.map((ch) => (
            <button
              key={ch.id}
              type="button"
              className={`deploy-approach-step__channel-btn${selectedChannelId === ch.id ? ' deploy-approach-step__channel-btn--active' : ''}`}
              onClick={() => setSelectedChannelId(ch.id)}
            >
              <span className="deploy-approach-step__channel-dot" />
              <span>{resolveLocalizedName(ch.name)}</span>
            </button>
          ))}
        </aside>

        {/* Approaches list */}
        <div className="deploy-approach-step__list">
          <div className="deploy-approach-step__search">
            <i className="pi pi-search" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('deploy.search.placeholder')}
            />
            <span className="deploy-approach-step__compat-count">
              {t('deploy.search.compatible', { count: visibleApproaches.length })}
            </span>
          </div>
          <div className="deploy-approach-step__approaches">
            {approachesLoading ? (
              <SpinnerAtom />
            ) : visibleApproaches.length === 0 ? (
              <EmptyStateAtom
                title={t('deploy.empty.title')}
                description={t('deploy.empty.subtitle')}
                icon="pi-box"
              />
            ) : (
              approachGroups.map((group) => (
                <section
                  key={group.key}
                  className="deploy-approach-step__group"
                  aria-label={group.label || undefined}
                  data-testid={`approach-group-${group.key}`}
                >
                  {group.label && <h3 className="deploy-approach-step__group-heading">{group.label}</h3>}
                  {group.items.map((a) => (
                    <button
                      key={a.system_name}
                      type="button"
                      data-testid={`approach-row-${a.system_name}`}
                      className={`deploy-approach-step__approach-row${selectedApproach?.system_name === a.system_name ? ' deploy-approach-step__approach-row--active' : ''}`}
                      onClick={() => handleSelectApproach(a)}
                    >
                      <span className="deploy-approach-step__approach-radio" />
                      <div className="deploy-approach-step__approach-info">
                        <span className="deploy-approach-step__approach-name">{a.name}</span>
                        {a.official && (
                          <ChipAtom label={t('addSphere.dialog.template.official')} icon="pi pi-verified" />
                        )}
                        {a.isNew && (
                          <ChipAtom label={t('addSphere.dialog.template.new')} />
                        )}
                        <p className="deploy-approach-step__approach-desc">{a.description}</p>
                        {a.channel && (
                          <span className="deploy-approach-step__approach-channel">{a.channel.name}</span>
                        )}
                      </div>
                    </button>
                  ))}
                </section>
              ))
            )}
          </div>
        </div>

        {/* Right detail */}
        <aside className="deploy-approach-step__detail" aria-label={t('deploy.detail.ariaLabel')}>
          {!selectedApproach ? (
            <div className="deploy-approach-step__detail-empty">
              <i className="pi pi-share-alt" />
              <div className="deploy-approach-step__detail-empty-title">{t('deploy.detail.empty.title')}</div>
              {lockedSphere && (
                <div className="deploy-approach-step__detail-empty-sub">
                  {t('deploy.detail.subtitle', { sphereName })}
                </div>
              )}
            </div>
          ) : !lockedSphere ? (
            /* Eligible sphere picker when approach selected but no locked sphere */
            <div className="deploy-approach-step__eligible" data-testid="eligible-sphere-picker">
              <div className="deploy-approach-step__eligible-title">{t('deploy.eligibleSpheres.title')}</div>
              {eligibleLoading ? (
                <SpinnerAtom />
              ) : eligibleSpheres.length === 0 ? (
                <div className="deploy-approach-step__eligible-empty">{t('deploy.eligibleSpheres.empty')}</div>
              ) : (
                eligibleSpheres.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    data-testid={`eligible-sphere-${s.id}`}
                    className="deploy-approach-step__eligible-sphere"
                    onClick={() => handleSelectEligibleSphere(s)}
                  >
                    {s.name}
                  </button>
                ))
              )}
            </div>
          ) : (
            /* Approach detail + deploy CTA */
            <div className="deploy-approach-step__approach-detail">
              <div className="deploy-approach-step__detail-header">
                <div className="deploy-approach-step__detail-name">{selectedApproach.name}</div>
                <div className="deploy-approach-step__detail-desc">{selectedApproach.description}</div>
              </div>
              <div className="deploy-approach-step__detail-body">
                <div className="deploy-approach-step__detail-section-label">
                  {t('deploy.detail.addedTo', { sphereName })}
                </div>
                {selectedApproach.factorCount > 0 && (
                  <div className="deploy-approach-step__detail-item">
                    <i className="pi pi-chart-bar" />
                    <span>{t('deploy.detail.statusFactors')}</span>
                    <span>{selectedApproach.factorCount}</span>
                  </div>
                )}
                {selectedApproach.childCount > 0 && (
                  <div className="deploy-approach-step__detail-item">
                    <i className="pi pi-sitemap" />
                    <span>{t('deploy.detail.childSpheres')}</span>
                    <span>{selectedApproach.childCount}</span>
                  </div>
                )}
                {selectedApproach.metaspaceCount > 0 && (
                  <div className="deploy-approach-step__detail-item">
                    <i className="pi pi-table" />
                    <span>{t('deploy.detail.metaspaces')}</span>
                    <span>{selectedApproach.metaspaceCount}</span>
                  </div>
                )}
              </div>
              {deployError && (
                <div className="deploy-approach-step__error">{deployError}</div>
              )}
              <div className="deploy-approach-step__detail-footer">
                <ButtonAtom
                  label={isDeploying ? t('deploy.cta.deploying') : t('deploy.cta.deploy', { sphereName })}
                  icon="pi pi-share-alt"
                  disabled={!canDeploy}
                  loading={isDeploying}
                  onClick={handleDeploy}
                />
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
};

export default DeployApproachStepMolecule;
